import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import './Users.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Users({ user }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banData, setBanData] = useState({ type: 'account', reason: '' });
  const [showScriptsModal, setShowScriptsModal] = useState(false);
  const [userScripts, setUserScripts] = useState(null);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/roles`, {
        withCredentials: true
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUserScripts = async (userId) => {
    setLoadingScripts(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/user-scripts/${userId}`, {
        withCredentials: true
      });
      setUserScripts(response.data);
      setShowScriptsModal(true);
    } catch (error) {
      toast.error('Failed to load user scripts');
    } finally {
      setLoadingScripts(false);
    }
  };

  const handleRemoveScript = async (scriptId) => {
    if (!selectedUser) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/user-scripts/${selectedUser.id}/${scriptId}`,
        { withCredentials: true }
      );
      toast.success('Script removed successfully');
      fetchUserScripts(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove script');
    }
  };

  const handleRoleChange = async (userId, newRoleId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, 
        { role_id: newRoleId },
        { withCredentials: true }
      );
      toast.success('Role updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleBan = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.post(`${API_URL}/api/admin/users/${selectedUser.id}/ban`,
        banData,
        { withCredentials: true }
      );
      toast.success(`User ${selectedUser.username} banned`);
      setShowBanModal(false);
      setSelectedUser(null);
      setBanData({ type: 'account', reason: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to ban user');
    }
  };

  const handleUnban = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}/ban`, {
        withCredentials: true
      });
      toast.success('User unbanned successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to unban user');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.discord_id?.includes(searchQuery);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="users-management">
      <div className="panel-header">
        <h1>User Management</h1>
        <div className="user-stats">
          <span className="stat-item"><span className="emoji">👥</span> {users.length} Total Users</span>
          <span className="stat-item"><span className="emoji">🛡️</span> {users.filter(u => u.role === 'admin').length} Admins</span>
          <span className="stat-item"><span className="emoji">🚫</span> {users.filter(u => u.is_banned).length} Banned</span>
        </div>
      </div>

      <div className="filters-bar glass">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or Discord ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="role-filters">
          <button 
            className={`filter-btn ${roleFilter === 'all' ? 'active' : ''}`}
            onClick={() => setRoleFilter('all')}
          >
            All ({users.length})
          </button>
          <button 
            className={`filter-btn ${roleFilter === 'admin' ? 'active' : ''}`}
            onClick={() => setRoleFilter('admin')}
          >
            Admins ({users.filter(u => u.role === 'admin').length})
          </button>
          <button 
            className={`filter-btn ${roleFilter === 'user' ? 'active' : ''}`}
            onClick={() => setRoleFilter('user')}
          >
            Users ({users.filter(u => u.role === 'user').length})
          </button>
        </div>
      </div>

      <div className="users-table-container glass">
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Discord Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>IP Address</th>
                <th>Joined</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className={u.is_banned ? 'banned-user' : ''}>
                  <td>
                    <img 
                      src={
                        u.avatar 
                          ? `https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png`
                          : `https://cdn.discordapp.com/embed/avatars/${parseInt(u.discord_id) % 5}.png`
                      }
                      alt={u.username}
                      className="user-avatar"
                      onError={(e) => {
                        e.target.src = `https://cdn.discordapp.com/embed/avatars/${parseInt(u.discord_id) % 5}.png`;
                      }}
                    />
                  </td>
                  <td>
                    <div className="user-info">
                      <span className="username">{u.username}</span>
                      <span className="discord-id">#{u.discord_id}</span>
                    </div>
                  </td>
                  <td className="email-cell">{u.email || 'N/A'}</td>
                  <td>
                    <select
                      value={u.role_id || ''}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      className="role-select-modern"
                      disabled={u.id === user.id}
                      style={{
                        '--role-color': roles.find(r => r.id === u.role_id)?.color || '#3b82f6'
                      }}
                    >
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.display_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="ip-cell">{u.last_ip || 'N/A'}</td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    {u.is_banned ? (
                      <span className="status-badge banned">
                        <span className="emoji">🚫</span> Banned ({u.ban_type})
                      </span>
                    ) : (
                      <span className="status-badge active"><span className="emoji">✅</span> Active</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => {
                          setSelectedUser(u);
                          fetchUserScripts(u.id);
                        }}
                        className="btn-view-scripts"
                        disabled={loadingScripts}
                      >
                        📦 Scripts
                      </button>
                      {!u.is_banned ? (
                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setShowBanModal(true);
                          }}
                          className="btn-ban"
                          disabled={u.id === user.id}
                        >
                          Ban
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnban(u.id)}
                          className="btn-unban"
                        >
                          Unban
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showBanModal && (
        <div className="modal-backdrop" onClick={() => setShowBanModal(false)}>
          <div className="ban-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ban User: {selectedUser?.username}</h2>
              <button className="close-btn" onClick={() => setShowBanModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Ban Type</label>
                <select
                  value={banData.type}
                  onChange={(e) => setBanData({ ...banData, type: e.target.value })}
                  className="ban-type-select"
                >
                  <option value="account">Account Ban (only this Discord account)</option>
                  <option value="ip">IP Ban (all accounts from this IP)</option>
                </select>
                <small className="help-text">
                  {banData.type === 'ip' 
                    ? '⚠️ IP ban will block all Discord accounts from this user\'s IP address'
                    : 'Account ban only blocks this specific Discord account'
                  }
                </small>
              </div>
              <div className="form-group">
                <label>Reason</label>
                <textarea
                  value={banData.reason}
                  onChange={(e) => setBanData({ ...banData, reason: e.target.value })}
                  placeholder="Enter ban reason..."
                  rows="3"
                  className="ban-reason"
                />
              </div>
              <div className="modal-actions">
                <button onClick={handleBan} className="btn btn-danger">
                  Confirm Ban
                </button>
                <button onClick={() => setShowBanModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showScriptsModal && userScripts && (
        <div className="modal-backdrop" onClick={() => setShowScriptsModal(false)}>
          <div className="scripts-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📦 {selectedUser?.username}'s Scripts</h2>
              <button className="close-btn" onClick={() => setShowScriptsModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="scripts-stats">
                <div className="stat-card">
                  <span className="stat-label">Total Scripts</span>
                  <span className="stat-value">{userScripts.count}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Total Value</span>
                  <span className="stat-value">{userScripts.totalValue.toFixed(2)} {userScripts.currency}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Roles Assigned</span>
                  <span className="stat-value">{userScripts.scripts.filter(s => s.roles_assigned).length}</span>
                </div>
              </div>

              {userScripts.scripts.length === 0 ? (
                <div className="empty-state">
                  <p>No scripts claimed yet</p>
                </div>
              ) : (
                <div className="scripts-list">
                  {userScripts.scripts.map((script) => (
                    <div key={script.id} className="script-item">
                      <div className="script-info">
                        <h4>{script.script_name}</h4>
                        <div className="script-meta">
                          <span>🆔 {script.transaction_id}</span>
                          <span>💰 {script.total_price} {script.currency}</span>
                          <span>📅 {new Date(script.verified_at).toLocaleDateString()}</span>
                          {script.roles_assigned ? (
                            <span className="role-badge assigned">✅ {script.discord_role_name || 'Role Assigned'}</span>
                          ) : (
                            <span className="role-badge pending">⏳ Pending</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveScript(script.id)}
                        className="btn-remove-script"
                        title="Remove this script"
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
